import * as msal from "@azure/msal-browser";
import { reactive } from "vue";

export interface MsalPluginOptions {
    clientId: string;
    loginAuthority: string;
    passwordAuthority: string;
    knownAuthority: string;
}

let msalInstance: msal.PublicClientApplication;

export let msalPluginInstance: MsalPlugin;

export class MsalPlugin {

    constructor(scope: string) {
        this._scope = scope;
    }

    private _scope: any;
    private pluginOptions: MsalPluginOptions = {
        clientId: "",
        loginAuthority: "",
        passwordAuthority: "",
        knownAuthority: ""
    };

    public isAuthenticated = false;

    // Vue 3: no global Vue / prototype. Make the instance reactive and publish
    // it as the shared singleton. `$msal` is exposed on globalProperties in
    // main.ts via a getter that returns this singleton. Methods are invoked
    // through the proxy so `this` mutations (e.g. isAuthenticated) stay reactive.
    public init(options?: MsalPluginOptions): void {
        if (!options) {
            throw new Error("MsalPluginOptions must be specified");
        }
        msalPluginInstance = reactive(this) as MsalPlugin;
        msalPluginInstance.pluginOptions = options;
        msalPluginInstance.initialize(options);
    }

    private async initialize(options: MsalPluginOptions) {
        const msalConfig: msal.Configuration = {
            auth: {
                clientId: options.clientId,
                authority: options.loginAuthority,
                knownAuthorities: [options.knownAuthority]
            }
        };
        msalInstance = new msal.PublicClientApplication(msalConfig);
        msalInstance.handleRedirectPromise().then((authResult: any) => {
            this.isAuthenticated = this.getIsAuthenticated();
            
        }).catch((err: any) => {
            const errorMessage = err.errorMessage || "";
            if (errorMessage.indexOf("AADB2C90118") > -1) {
                msalInstance.loginRedirect({
                    scopes: ["openid", "profile", "offline_access"],
                    authority: this.pluginOptions.passwordAuthority
                });
            } else {
                this.isAuthenticated = false;
                // User cancelled the login (redirect flow returns access_denied / AADB2C90090 / AADB2C90091).
                // Popup flow uses 'user_cancelled'. In all cases, send them back to sign in.
                const cancelled = err.errorCode === 'user_cancelled'
                    || err.errorCode === 'access_denied'
                    || errorMessage.indexOf("AADB2C90091") > -1
                    || errorMessage.indexOf("AADB2C90090") > -1;
                if (cancelled) {
                    this.signIn();
                }
            }
        });
        this.isAuthenticated = this.getIsAuthenticated();
    }

    public account() {
        return msalInstance.getAllAccounts()[0];
    }

    public async signIn() {
        const loginRequest: msal.PopupRequest = {
            scopes: ["openid", "profile", "offline_access",this._scope],
        };
        try {
            await msalInstance.loginRedirect(loginRequest);
        } catch (error: any) {
            if (error?.errorCode !== 'interaction_in_progress') throw error;
        }
    }

    public async changePassword() {
        try {
            await msalInstance.loginRedirect({
                scopes: ["openid", "profile", "offline_access", this._scope],
                authority: this.pluginOptions.passwordAuthority
            });
        } catch (error: any) {
            if (error?.errorCode !== 'interaction_in_progress') throw error;
        }
    }

    public async signOut() {
        await msalInstance.logout();
        this.isAuthenticated = false;
    }

    public async acquireToken() {
        const request = {
            account: msalInstance.getAllAccounts()[0],
            scopes: [this._scope]
        };
        try {
            const response = await msalInstance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            if (error instanceof msal.InteractionRequiredAuthError) {
                try {
                    await msalInstance.acquireTokenRedirect(request);
                } catch (redirectError: any) {
                    if (redirectError?.errorCode !== 'interaction_in_progress') {
                        throw redirectError;
                    }
                }
            }
            return false;
        }
    }

    private getIsAuthenticated(): boolean {
        const accounts: msal.AccountInfo[] = msalInstance.getAllAccounts();
        return accounts && accounts.length > 0;
    }
}